/* eslint-disable react/no-array-index-key */
'use client';

import { useRouter, useSearchParams } from 'next/navigation';
import React, { useEffect, useState } from 'react';

import Button from 'molecules/Button';
import Icon from 'molecules/Icon';

import styles from 'components/ListingDeck/Pagination/pagination.module.scss';

interface PaginationProps {
  totalItems: number;
  limit: number;
  currentPage: number;
}

const Pagination: React.FC<PaginationProps> = ({ totalItems, limit, currentPage }) => {
  const router = useRouter();
  const searchParams = useSearchParams();
  const [totalPages, setTotalPages] = useState(0);

  useEffect(() => {
    const pages = Math.ceil(totalItems / limit);
    setTotalPages(pages);
  }, [totalItems, limit]);

  const handlePageClick = (page: number) => {
    const newSearchParams = new URLSearchParams(searchParams.toString());
    newSearchParams.set('p', page.toString());

    router.push(`?${newSearchParams.toString()}`, { scroll: true });
  };

  const getPageNumbers = () => {
    const pageNumbersToShow = 5;
    const pages: (number | string)[] = [];

    if (totalPages <= pageNumbersToShow + 2) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    pages.push(1);

    if (currentPage <= 3) {
      pages.push(2, 3, 4, 5);
      pages.push('...');
      pages.push(totalPages);
    } else if (currentPage >= totalPages - 2) {
      pages.push('...');
      pages.push(totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages);
    } else {
      pages.push('...');
      pages.push(currentPage - 1, currentPage, currentPage + 1);
      pages.push('...');
      pages.push(totalPages);
    }

    return pages;
  };

  return (
    <div className={styles.pagination}>
      <Button
        className={styles.prevLink}
        direction="right"
        onClick={() => handlePageClick(Math.max(currentPage - 1, 1))}
        disabled={currentPage === 1}
      >
        <Icon size={19} icon="arrow-right" />
        Previous
      </Button>
      <div className={styles.paginationNumberWrapper}>
        {getPageNumbers()?.map((page, index) =>
          page === '...' ? (
            <span key={`ellipsis-${index}`} className={styles.ellipsis}>
              ...
            </span>
          ) : (
            <button
              className={`${styles.paginationNumber} ${currentPage === page ? styles.active : ''}`}
              key={page}
              type="button"
              data-qa="option"
              data-key={page}
              onClick={() => handlePageClick(page as number)}
            >
              <div data-qa="label">{page}</div>
            </button>
          ),
        )}
      </div>
      <Button
        className={styles.arrowLink}
        direction="right"
        iconId="arrow-right"
        onClick={() => handlePageClick(Math.min(currentPage + 1, totalPages))}
        disabled={currentPage === totalPages}
      >
        Next
      </Button>
    </div>
  );
};

export default Pagination;
